// Sonderteile und Zerbrechliches (08.10.2026), gespeichert als Liste an der Aufnahme (surveys.special_items); die Firma
// sieht beides als eigenen Abschnitt im Bericht.
//
// Seit 30.10.2026 eine Seite statt drei (Betreiber: „alle Schritte pragmatisch vereinfachen“): beide Listen als Felder
// zum Antippen, Anzahl und Notiz erst nach dem Antippen. Das Markieren im Foto (kommt nicht mit, entsorgen, ab- und
// aufbauen) ist ein Link auf diese Seite. Waschmaschine/Trockner stehen bei der Küche und hier nicht mehr zur Wahl.

import { SPECIAL_ITEMS, type SpecialItem, type SpecialKind } from "@umzugsscan/shared";
import { useDraft, useStore } from "../hooks.ts";
import { useNav } from "../Erfassung.tsx";
import { Footer, Stepper } from "../ui/elemente.tsx";

const TITLE: Record<SpecialKind, { title: string; hint: string }> = {
  sonderteil: { title: "Schwer oder sperrig", hint: "Braucht besondere Ausrüstung oder mehr Helfer." },
  zerbrechlich: { title: "Zerbrechlich", hint: "Damit genug Polstermaterial und passende Kisten eingeplant werden." },
};
/** Steht schon bei der Küche (Geräte); bleibt für ältere Aufnahmen im Bericht lesbar. */
const NICHT_HIER = new Set(["waschmaschine"]);

export function Besonderes() {
  const d = useDraft();
  const store = useStore();
  const { weiter, zurueck, go } = useNav();
  const all = d.survey.specialItems ?? [];
  const save = (next: SpecialItem[]) => store.updateSurvey({ specialItems: next });
  const find = (kind: SpecialKind, key: string) => all.find((i) => i.kind === kind && i.key === key);
  const toggle = (kind: SpecialKind, key: string) =>
    save(find(kind, key) ? all.filter((i) => !(i.kind === kind && i.key === key)) : [...all, { kind, key, qty: 1, note: null }]);
  const change = (item: SpecialItem, patch: Partial<SpecialItem>) => save(all.map((i) => (i === item ? { ...i, ...patch } : i)));
  const marks = d.photos.reduce((n, p) => n + p.marks.length, 0);

  return (
    <>
      <h1>Gibt es etwas Besonderes?</h1>
      <p className="lead">Tippen Sie an, was mitkommt. Nichts davon? Dann einfach weiter.</p>
      {(["sonderteil", "zerbrechlich"] as const).map((kind) => {
        const liste = SPECIAL_ITEMS[kind].filter((e) => !NICHT_HIER.has(e.key));
        const gewaehlt = all.filter((i) => i.kind === kind);
        return (
          <section className="card" key={kind}>
            <h2>{TITLE[kind].title}</h2>
            <p className="hint">{TITLE[kind].hint}</p>
            <div className="chips" role="group" aria-label={TITLE[kind].title}>
              {liste.map((e) => {
                const on = !!find(kind, e.key);
                return (
                  <button key={e.key} type="button" className={`chip${on ? " on" : ""}`} aria-pressed={on} onClick={() => toggle(kind, e.key)}>
                    {on ? "✓ " : ""}
                    {e.label}
                  </button>
                );
              })}
            </div>
            {gewaehlt.map((item) => {
              const e = SPECIAL_ITEMS[kind].find((x) => x.key === item.key);
              return (
                <div className="besonders-details" key={item.key}>
                  <Stepper label={e?.label ?? item.key} value={item.qty} min={1} max={99} onChange={(qty) => change(item, { qty })} />
                  <input
                    className="sheet-input"
                    placeholder={e?.hint ? `Was genau? (optional, ${e.hint})` : "Was genau? (optional, z. B. Größe, Raum)"}
                    maxLength={200}
                    value={item.note ?? ""}
                    onChange={(ev) => change(item, { note: ev.target.value || null })}
                  />
                </div>
              );
            })}
          </section>
        );
      })}
      <section className="card">
        <h2>Einzelne Möbel</h2>
        <p className="hint">Soll ein Möbelstück nicht mit, entsorgt oder ab- und wieder aufgebaut werden? Tippen Sie es im Foto an.</p>
        <button type="button" className="link" onClick={() => go({ screen: "markieren" })}>
          {marks > 0 ? `${marks} markiert – ansehen oder ändern` : "Möbel im Foto markieren"}
        </button>
      </section>
      <Footer onBack={zurueck} onNext={weiter} nextLabel={all.length > 0 || marks > 0 ? "Weiter" : "Nichts davon – weiter"} />
    </>
  );
}
